import { useCallback, useEffect, useRef, useState } from 'react'
import i18n from '@/i18n'
import { PREVIEW_PROTOCOL_VERSION, PreviewMessage } from '../schemas/protocol'
import { diffPreviewConfiguration, mergePreviewPatches } from '../utils/previewDiff'

// The bridge negotiates historical slide previews and generic collection previews.

const EMPTY_ASSET_URLS = Object.freeze({})

function channelId() {
  return globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function usePreviewBridge({
  iframeRef,
  templateId,
  configuration,
  selectedSlide,
  onSelectSlide,
  selectedItems = {},
  onSelectItem,
  protocolVersion = PREVIEW_PROTOCOL_VERSION,
}) {
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)
  const [diagnostics, setDiagnostics] = useState([])
  const [diagnosticConfiguration, setDiagnosticConfiguration] = useState(null)
  const sentConfigurationRef = useRef(null)
  const readyRef = useRef(false)
  const configurationRef = useRef(configuration)
  const observedConfigurationRef = useRef(configuration)
  const channelRef = useRef(channelId())
  const revisionRef = useRef(0)
  const readyTimeoutRef = useRef(null)
  const sendFrameRef = useRef(null)
  const sentAssetUrlsRef = useRef(null)
  const selectedSlideRef = useRef(selectedSlide)
  const selectedItemsRef = useRef(selectedItems)
  const capabilitiesRef = useRef({ configurationPatches: false, rendererApiVersion: 1 })
  const pendingRef = useRef(null)
  const inFlightRef = useRef(null)
  const restartPendingRef = useRef(false)

  configurationRef.current = configuration
  selectedSlideRef.current = selectedSlide
  selectedItemsRef.current = selectedItems

  const post = useCallback((type, payload = {}) => {
    const target = iframeRef.current?.contentWindow
    if (!target || !templateId) return
    target.postMessage({
      type,
      protocolVersion,
      templateId,
      channel: channelRef.current,
      ...payload,
    }, '*')
  }, [iframeRef, protocolVersion, templateId])

  const scheduleSend = useCallback(() => {
    if (sendFrameRef.current !== null) return
    sendFrameRef.current = window.requestAnimationFrame(() => {
      sendFrameRef.current = null
      if (!readyRef.current || inFlightRef.current) return

      if (restartPendingRef.current) {
        restartPendingRef.current = false
        revisionRef.current += 1
        inFlightRef.current = { revision: revisionRef.current, kind: 'restart' }
        setStatus('rendering')
        setError(null)
        post(PreviewMessage.RESTART, { revision: revisionRef.current })
        return
      }

      const pending = pendingRef.current
      if (!pending) return
      pendingRef.current = null
      sentConfigurationRef.current = pending.configuration
      revisionRef.current += 1
      const revision = revisionRef.current
      const supportsPatches = capabilitiesRef.current.configurationPatches
        && Number(capabilitiesRef.current.rendererApiVersion) >= 2

      if (supportsPatches && pending.kind === 'patch' && pending.operations.length) {
        inFlightRef.current = { revision, kind: 'patch' }
        sentAssetUrlsRef.current = pending.operations.some(({ path }) => path === 'assetUrls')
          ? (pending.configuration.assetUrls || EMPTY_ASSET_URLS)
          : sentAssetUrlsRef.current
        setStatus('ready')
        setError(null)
        post(PreviewMessage.PATCH, {
          revision,
          operations: pending.operations,
          selectedSlide: selectedSlideRef.current,
          selectedItems: selectedItemsRef.current,
        })
        return
      }

      const assetUrls = pending.configuration.assetUrls || EMPTY_ASSET_URLS
      const assetUrlsUnchanged = sentAssetUrlsRef.current === assetUrls
      const nextConfiguration = assetUrlsUnchanged
        ? Object.fromEntries(
          Object.entries(pending.configuration).filter(([key]) => key !== 'assetUrls'),
        )
        : pending.configuration
      sentAssetUrlsRef.current = assetUrls
      inFlightRef.current = { revision, kind: 'full' }
      setStatus('rendering')
      setError(null)
      post(PreviewMessage.UPDATE, {
        revision,
        configuration: nextConfiguration,
        assetUrlsUnchanged,
        selectedSlide: selectedSlideRef.current,
        selectedItems: selectedItemsRef.current,
      })
    })
  }, [post])

  const initialize = useCallback(() => {
    if (!configurationRef.current) return
    pendingRef.current = null
    observedConfigurationRef.current = configurationRef.current
    sentConfigurationRef.current = configurationRef.current
    sentAssetUrlsRef.current = configurationRef.current.assetUrls || EMPTY_ASSET_URLS
    revisionRef.current += 1
    inFlightRef.current = { revision: revisionRef.current, kind: 'initialize' }
    post(PreviewMessage.INIT, {
      revision: revisionRef.current,
      configuration: configurationRef.current,
      selectedSlide: selectedSlideRef.current,
      selectedItems: selectedItemsRef.current,
    })
    setStatus('rendering')
    setError(null)
  }, [post])

  useEffect(() => {
    readyRef.current = false
    channelRef.current = channelId()
    revisionRef.current = 0
    sentAssetUrlsRef.current = null
    capabilitiesRef.current = { configurationPatches: false, rendererApiVersion: 1 }
    pendingRef.current = null
    inFlightRef.current = null
    restartPendingRef.current = false
    observedConfigurationRef.current = configurationRef.current
    if (sendFrameRef.current !== null) window.cancelAnimationFrame(sendFrameRef.current)
    sendFrameRef.current = null
    setStatus('loading')
    setError(null)
    setDiagnostics([])
  }, [protocolVersion, templateId])

  useEffect(() => {
    function completeRevision(message) {
      const inFlight = inFlightRef.current
      if (!inFlight || Number(message.revision) < inFlight.revision) return
      inFlightRef.current = null
      setStatus('ready')
      setError(null)
      scheduleSend()
    }

    function receive(event) {
      if (event.source !== iframeRef.current?.contentWindow) return
      const message = event.data || {}
      if (
        message.protocolVersion !== protocolVersion
        || message.templateId !== templateId
      ) return

      if (message.type === PreviewMessage.READY) {
        if (readyTimeoutRef.current) window.clearTimeout(readyTimeoutRef.current)
        capabilitiesRef.current = {
          configurationPatches: message.capabilities?.configurationPatches === true,
          rendererApiVersion: Number(message.capabilities?.rendererApiVersion || 1),
        }
        readyRef.current = true
        initialize()
        return
      }
      if (message.channel !== channelRef.current) return
      if (
        message.type === PreviewMessage.RENDERED
        || message.type === PreviewMessage.PATCHED
      ) {
        completeRevision(message)
      } else if (message.type === PreviewMessage.SLIDE_SELECTED) {
        onSelectSlide?.(Number(message.slideIndex || 0))
      } else if (message.type === PreviewMessage.COLLECTION_ITEM_SELECTED) {
        onSelectItem?.(String(message.collectionPath || ''), String(message.itemId || ''))
      } else if (message.type === PreviewMessage.LAYOUT_DIAGNOSTICS) {
        if (Number(message.revision) !== revisionRef.current) return
        setDiagnostics(Array.isArray(message.diagnostics) ? message.diagnostics : [])
        setDiagnosticConfiguration(sentConfigurationRef.current)
      } else if (message.type === PreviewMessage.ERROR) {
        if (Number(message.revision ?? revisionRef.current) < revisionRef.current) return
        if (
          inFlightRef.current
          && Number(message.revision ?? inFlightRef.current.revision) >= inFlightRef.current.revision
        ) {
          inFlightRef.current = null
        }
        setStatus('error')
        setError({
          phase: message.phase || 'runtime',
          message: message.message || i18n.t('editorV2.shell.preview.renderFailed'),
        })
        scheduleSend()
      }
    }

    window.addEventListener('message', receive)
    return () => {
      window.removeEventListener('message', receive)
      if (readyTimeoutRef.current) window.clearTimeout(readyTimeoutRef.current)
    }
  }, [iframeRef, initialize, onSelectItem, onSelectSlide, protocolVersion, scheduleSend, templateId])

  useEffect(() => {
    if (!configuration) return
    const previous = observedConfigurationRef.current
    observedConfigurationRef.current = configuration
    if (!previous || previous === configuration) return
    const change = diffPreviewConfiguration(previous, configuration)
    if (change.kind === 'patch' && !change.operations.length) {
      // Same content under a new object (e.g. an applied generation identical to the ad): the
      // frame already checked it, so its diagnostics stand for this object too.
      setDiagnosticConfiguration((current) => (current === previous ? configuration : current))
      return
    }
    pendingRef.current = mergePreviewPatches(pendingRef.current, change, configuration)
    scheduleSend()
  }, [configuration, scheduleSend])

  useEffect(() => {
    if (readyRef.current) {
      post(PreviewMessage.SELECT_SLIDE, { slideIndex: selectedSlide })
    }
  }, [post, selectedSlide])

  useEffect(() => {
    if (!readyRef.current || protocolVersion < 2) return
    Object.entries(selectedItems || {}).forEach(([collectionPath, itemId]) => {
      post(PreviewMessage.SELECT_COLLECTION_ITEM, { collectionPath, itemId })
    })
  }, [post, protocolVersion, selectedItems])

  const restart = useCallback(() => {
    if (!readyRef.current) return
    restartPendingRef.current = true
    scheduleSend()
  }, [scheduleSend])

  useEffect(() => () => {
    if (sendFrameRef.current !== null) window.cancelAnimationFrame(sendFrameRef.current)
  }, [])

  return {
    status,
    error,
    diagnostics,
    diagnosticConfiguration,
    restart,
    onFrameLoad: () => {
      if (readyRef.current) return
      setStatus('loading')
      if (readyTimeoutRef.current) window.clearTimeout(readyTimeoutRef.current)
      readyTimeoutRef.current = window.setTimeout(() => {
        if (!readyRef.current) {
          setStatus('error')
          setError({
            phase: 'initialize',
            message: i18n.t('editorV2.shell.preview.notReady'),
          })
        }
      }, 5000)
    },
  }
}
